import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold ring-offset-background transition-all duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary — Electric Blue with glow
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 shadow-glow-blue hover:shadow-glow-blue-lg hover:-translate-y-0.5",
        // Secondary — Neon outline
        secondary:
          "bg-transparent text-primary border-2 border-primary hover:bg-primary hover:text-primary-foreground hover:shadow-glow-blue",
        // Destructive — Red
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 hover:shadow-glow-red",
        // Ghost — subtle
        ghost:
          "bg-transparent text-foreground border border-white/20 hover:border-primary hover:text-primary hover:bg-primary/5",
        // Outline — border only
        outline:
          "border border-[rgba(0,212,255,0.3)] bg-transparent text-primary hover:bg-primary/10 hover:border-primary",
        // Link
        link: "text-primary underline-offset-4 hover:underline",
        // Neon gradient
        neon: "bg-gradient-to-r from-primary to-secondary text-primary-foreground font-bold hover:scale-105 hover:brightness-110 shadow-glow-blue",
        // Glow accent
        glow: "bg-primary/20 text-primary border border-primary/50 hover:bg-primary/30 shadow-glow-blue",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3 text-xs",
        lg: "h-11 rounded-md px-8",
        xl: "h-14 rounded-lg px-10 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
